<template>
  <SectionTabHorizLogo03 :title="t('title')" :tabs="tabs" theme="cyan" />
</template>

<script setup lang="ts">
  import type { IItem } from '@/components/Shared/Section/Tab/HorizLogo03.vue';

  const LOGO_NAMES = ['mirai', 'yageo-group', 'tct'];

  const { t } = useI18n({ useScope: 'local' });
  const { blobDomain } = useWebsite();
  const { provideResponsiveImgName } = useImagePath();

  const provideImage = (fileName: string): string =>
    `${blobDomain.value}/wise-paas-marketplace/topics2/doe/${fileName}`;

  const tabs = computed((): IItem[] =>
    [1, 2, 3].map((index) => ({
      tabName: t(`item-${index}-tab-name`),
      logo: provideImage(
        `use-case-${index}-${LOGO_NAMES[index - 1]}-logo.webp`
      ),
      name: t(`item-${index}-name`),
      metrics: [1, 2, 3].map((metric) => ({
        name: t(`item-${index}-metric-${metric}-value`),
        description: t(`item-${index}-metric-${metric}-description`),
      })),
      linkName: t('item-button'),
      link: t(`item-${index}-link`),
      thumbnail: provideImage(provideResponsiveImgName(`use-case-${index}`)),
    }))
  );
</script>

<i18n lang="json">
{
  "en-int": {
    "title": "Explore Digital O&M Use Cases",
    "item-button": "Learn More",
    "item-1-tab-name": "Solar PV Monitoring",
    "item-1-name": "Singapore’s leading solar PV manufacturer, Mirai Electronics, builds its next-gen remote monitoring system with Advantech WISE.",
    "item-1-metric-1-value": "400 +",
    "item-1-metric-1-description": "Centralized monitoring across 400+ solar PV sites gives operators unified visibility and supports efficient remote operations at scale.",
    "item-1-metric-2-value": "90% Faster",
    "item-1-metric-2-description": "Remote provisioning and configuration streamline onboarding, reduce manual setup, and help teams bring distributed sites online 90% faster.",
    "item-1-metric-3-value": "24/7",
    "item-1-metric-3-description": "Continuous site visibility helps teams detect issues earlier, respond faster, and maintain stable operations across distributed solar PV assets.",
    "item-1-link": "/en-int/materials/mirai-electronics-singapores-leading-solar-photovoltaic-manufacturer-builds-next-gen-solar-pv-monitoring-and-metering-system-with-edgehub",
    "item-2-tab-name": "Electronics Manufacturing",
    "item-2-name": "From intelligent to low carbon, Advantech WISE powers YAGEO’s manufacturing transformation.",
    "item-2-metric-1-value": "10%",
    "item-2-metric-1-description": "Real-time equipment visibility and OEE analysis help teams identify utilization gaps, resolve bottlenecks, and improve production performance.",
    "item-2-metric-2-value": "24/7",
    "item-2-metric-2-description": "Continuous monitoring detects abnormal shutdowns early, alerts responsible teams, and enables faster action to reduce unplanned downtime.",
    "item-2-metric-3-value": "Data-Driven",
    "item-2-metric-3-description": "Operational and energy data support smarter decisions, continuous production improvement, and practical long-term carbon-reduction planning.",
    "item-2-link": "/en-int/materials/intelligent-low-carbon-wise-iot-yageo-manufacturing-transformation",
    "item-3-tab-name": "Industrial OEM",
    "item-3-name": "Advantech WISE empowers manufacturers with smart, scalable predictive maintenance solutions.",
    "item-3-metric-1-value": "7-Day",
    "item-3-metric-1-description": "Predictive alerts provide up to seven days of advance warning, giving maintenance teams more time to address potential equipment issues.",
    "item-3-metric-2-value": "High-load",
    "item-3-metric-2-description": "Equipment monitoring under high-load conditions helps teams maintain stable operation and respond faster in demanding industrial environments.",
    "item-3-metric-3-value": "Readiness",
    "item-3-metric-3-description": "Real-time health reports and predictive alerts keep maintenance teams informed of equipment conditions and potential abnormalities.",
    "item-3-link": "/en-int/materials/tct-integral-wise-iot-predictive-maintenance"
  },
  "en-jp": {
    "title": "Explore Digital O&M Use Cases",
    "item-button": "Learn More",
    "item-1-tab-name": "Solar PV Monitoring",
    "item-1-name": "Singapore’s leading solar PV manufacturer, Mirai Electronics, builds its next-gen remote monitoring system with Advantech WISE.",
    "item-1-metric-1-value": "400 +",
    "item-1-metric-1-description": "Centralized monitoring across 400+ solar PV sites gives operators unified visibility and supports efficient remote operations at scale.",
    "item-1-metric-2-value": "90% Faster",
    "item-1-metric-2-description": "Remote provisioning and configuration streamline onboarding, reduce manual setup, and help teams bring distributed sites online 90% faster.",
    "item-1-metric-3-value": "24/7",
    "item-1-metric-3-description": "Continuous site visibility helps teams detect issues earlier, respond faster, and maintain stable operations across distributed solar PV assets.",
    "item-1-link": "/en-jp/materials/mirai-electronics-singapores-leading-solar-photovoltaic-manufacturer-builds-next-gen-solar-pv-monitoring-and-metering-system-with-edgehub",
    "item-2-tab-name": "Electronics Manufacturing",
    "item-2-name": "From intelligent to low carbon, Advantech WISE powers YAGEO’s manufacturing transformation.",
    "item-2-metric-1-value": "10%",
    "item-2-metric-1-description": "Real-time equipment visibility and OEE analysis help teams identify utilization gaps, resolve bottlenecks, and improve production performance.",
    "item-2-metric-2-value": "24/7",
    "item-2-metric-2-description": "Continuous monitoring detects abnormal shutdowns early, alerts responsible teams, and enables faster action to reduce unplanned downtime.",
    "item-2-metric-3-value": "Data-Driven",
    "item-2-metric-3-description": "Operational and energy data support smarter decisions, continuous production improvement, and practical long-term carbon-reduction planning.",
    "item-2-link": "/en-jp/materials/intelligent-low-carbon-wise-iot-yageo-manufacturing-transformation",
    "item-3-tab-name": "Industrial OEM",
    "item-3-name": "Advantech WISE empowers manufacturers with smart, scalable predictive maintenance solutions.",
    "item-3-metric-1-value": "7-Day",
    "item-3-metric-1-description": "Predictive alerts provide up to seven days of advance warning, giving maintenance teams more time to address potential equipment issues.",
    "item-3-metric-2-value": "High-load",
    "item-3-metric-2-description": "Equipment monitoring under high-load conditions helps teams maintain stable operation and respond faster in demanding industrial environments.",
    "item-3-metric-3-value": "Readiness",
    "item-3-metric-3-description": "Real-time health reports and predictive alerts keep maintenance teams informed of equipment conditions and potential abnormalities.",
    "item-3-link": "/en-jp/materials/tct-integral-wise-iot-predictive-maintenance"
  },
  "en-tw": {
    "title": "Explore Digital O&M Use Cases",
    "item-button": "Learn More",
    "item-1-tab-name": "Solar PV Monitoring",
    "item-1-name": "Singapore’s leading solar PV manufacturer, Mirai Electronics, builds its next-gen remote monitoring system with Advantech WISE.",
    "item-1-metric-1-value": "400 +",
    "item-1-metric-1-description": "Centralized monitoring across 400+ solar PV sites gives operators unified visibility and supports efficient remote operations at scale.",
    "item-1-metric-2-value": "90% Faster",
    "item-1-metric-2-description": "Remote provisioning and configuration streamline onboarding, reduce manual setup, and help teams bring distributed sites online 90% faster.",
    "item-1-metric-3-value": "24/7",
    "item-1-metric-3-description": "Continuous site visibility helps teams detect issues earlier, respond faster, and maintain stable operations across distributed solar PV assets.",
    "item-1-link": "/en-tw/materials/mirai-electronics-singapores-leading-solar-photovoltaic-manufacturer-builds-next-gen-solar-pv-monitoring-and-metering-system-with-edgehub",
    "item-2-tab-name": "Electronics Manufacturing",
    "item-2-name": "From intelligent to low carbon, Advantech WISE powers YAGEO’s manufacturing transformation.",
    "item-2-metric-1-value": "10%",
    "item-2-metric-1-description": "Real-time equipment visibility and OEE analysis help teams identify utilization gaps, resolve bottlenecks, and improve production performance.",
    "item-2-metric-2-value": "24/7",
    "item-2-metric-2-description": "Continuous monitoring detects abnormal shutdowns early, alerts responsible teams, and enables faster action to reduce unplanned downtime.",
    "item-2-metric-3-value": "Data-Driven",
    "item-2-metric-3-description": "Operational and energy data support smarter decisions, continuous production improvement, and practical long-term carbon-reduction planning.",
    "item-2-link": "/en-tw/materials/intelligent-low-carbon-wise-iot-yageo-manufacturing-transformation",
    "item-3-tab-name": "Industrial OEM",
    "item-3-name": "Advantech WISE empowers manufacturers with smart, scalable predictive maintenance solutions.",
    "item-3-metric-1-value": "7-Day",
    "item-3-metric-1-description": "Predictive alerts provide up to seven days of advance warning, giving maintenance teams more time to address potential equipment issues.",
    "item-3-metric-2-value": "High-load",
    "item-3-metric-2-description": "Equipment monitoring under high-load conditions helps teams maintain stable operation and respond faster in demanding industrial environments.",
    "item-3-metric-3-value": "Readiness",
    "item-3-metric-3-description": "Real-time health reports and predictive alerts keep maintenance teams informed of equipment conditions and potential abnormalities.",
    "item-3-link": "/en-tw/materials/tct-integral-wise-iot-predictive-maintenance"
  },
  "en-kr": {
    "title": "Explore Digital O&M Use Cases",
    "item-button": "Learn More",
    "item-1-tab-name": "Solar PV Monitoring",
    "item-1-name": "Singapore’s leading solar PV manufacturer, Mirai Electronics, builds its next-gen remote monitoring system with Advantech WISE.",
    "item-1-metric-1-value": "400 +",
    "item-1-metric-1-description": "Centralized monitoring across 400+ solar PV sites gives operators unified visibility and supports efficient remote operations at scale.",
    "item-1-metric-2-value": "90% Faster",
    "item-1-metric-2-description": "Remote provisioning and configuration streamline onboarding, reduce manual setup, and help teams bring distributed sites online 90% faster.",
    "item-1-metric-3-value": "24/7",
    "item-1-metric-3-description": "Continuous site visibility helps teams detect issues earlier, respond faster, and maintain stable operations across distributed solar PV assets.",
    "item-1-link": "/en-kr/materials/mirai-electronics-singapores-leading-solar-photovoltaic-manufacturer-builds-next-gen-solar-pv-monitoring-and-metering-system-with-edgehub",
    "item-2-tab-name": "Electronics Manufacturing",
    "item-2-name": "From intelligent to low carbon, Advantech WISE powers YAGEO’s manufacturing transformation.",
    "item-2-metric-1-value": "10%",
    "item-2-metric-1-description": "Real-time equipment visibility and OEE analysis help teams identify utilization gaps, resolve bottlenecks, and improve production performance.",
    "item-2-metric-2-value": "24/7",
    "item-2-metric-2-description": "Continuous monitoring detects abnormal shutdowns early, alerts responsible teams, and enables faster action to reduce unplanned downtime.",
    "item-2-metric-3-value": "Data-Driven",
    "item-2-metric-3-description": "Operational and energy data support smarter decisions, continuous production improvement, and practical long-term carbon-reduction planning.",
    "item-2-link": "/en-kr/materials/intelligent-low-carbon-wise-iot-yageo-manufacturing-transformation",
    "item-3-tab-name": "Industrial OEM",
    "item-3-name": "Advantech WISE empowers manufacturers with smart, scalable predictive maintenance solutions.",
    "item-3-metric-1-value": "7-Day",
    "item-3-metric-1-description": "Predictive alerts provide up to seven days of advance warning, giving maintenance teams more time to address potential equipment issues.",
    "item-3-metric-2-value": "High-load",
    "item-3-metric-2-description": "Equipment monitoring under high-load conditions helps teams maintain stable operation and respond faster in demanding industrial environments.",
    "item-3-metric-3-value": "Readiness",
    "item-3-metric-3-description": "Real-time health reports and predictive alerts keep maintenance teams informed of equipment conditions and potential abnormalities.",
    "item-3-link": "/en-kr/materials/tct-integral-wise-iot-predictive-maintenance"
  },
  "en-americas": {
    "title": "Explore Digital O&M Use Cases",
    "item-button": "Learn More",
    "item-1-tab-name": "Solar PV Monitoring",
    "item-1-name": "Singapore’s leading solar PV manufacturer, Mirai Electronics, builds its next-gen remote monitoring system with Advantech WISE.",
    "item-1-metric-1-value": "400 +",
    "item-1-metric-1-description": "Centralized monitoring across 400+ solar PV sites gives operators unified visibility and supports efficient remote operations at scale.",
    "item-1-metric-2-value": "90% Faster",
    "item-1-metric-2-description": "Remote provisioning and configuration streamline onboarding, reduce manual setup, and help teams bring distributed sites online 90% faster.",
    "item-1-metric-3-value": "24/7",
    "item-1-metric-3-description": "Continuous site visibility helps teams detect issues earlier, respond faster, and maintain stable operations across distributed solar PV assets.",
    "item-1-link": "/en-americas/materials/mirai-electronics-singapores-leading-solar-photovoltaic-manufacturer-builds-next-gen-solar-pv-monitoring-and-metering-system-with-edgehub",
    "item-2-tab-name": "Electronics Manufacturing",
    "item-2-name": "From intelligent to low carbon, Advantech WISE powers YAGEO’s manufacturing transformation.",
    "item-2-metric-1-value": "10%",
    "item-2-metric-1-description": "Real-time equipment visibility and OEE analysis help teams identify utilization gaps, resolve bottlenecks, and improve production performance.",
    "item-2-metric-2-value": "24/7",
    "item-2-metric-2-description": "Continuous monitoring detects abnormal shutdowns early, alerts responsible teams, and enables faster action to reduce unplanned downtime.",
    "item-2-metric-3-value": "Data-Driven",
    "item-2-metric-3-description": "Operational and energy data support smarter decisions, continuous production improvement, and practical long-term carbon-reduction planning.",
    "item-2-link": "/en-americas/materials/intelligent-low-carbon-wise-iot-yageo-manufacturing-transformation",
    "item-3-tab-name": "Industrial OEM",
    "item-3-name": "Advantech WISE empowers manufacturers with smart, scalable predictive maintenance solutions.",
    "item-3-metric-1-value": "7-Day",
    "item-3-metric-1-description": "Predictive alerts provide up to seven days of advance warning, giving maintenance teams more time to address potential equipment issues.",
    "item-3-metric-2-value": "High-load",
    "item-3-metric-2-description": "Equipment monitoring under high-load conditions helps teams maintain stable operation and respond faster in demanding industrial environments.",
    "item-3-metric-3-value": "Readiness",
    "item-3-metric-3-description": "Real-time health reports and predictive alerts keep maintenance teams informed of equipment conditions and potential abnormalities.",
    "item-3-link": "/en-americas/materials/tct-integral-wise-iot-predictive-maintenance"
  },
  "en-asia-pacific": {
    "title": "Explore Digital O&M Use Cases",
    "item-button": "Learn More",
    "item-1-tab-name": "Solar PV Monitoring",
    "item-1-name": "Singapore’s leading solar PV manufacturer, Mirai Electronics, builds its next-gen remote monitoring system with Advantech WISE.",
    "item-1-metric-1-value": "400 +",
    "item-1-metric-1-description": "Centralized monitoring across 400+ solar PV sites gives operators unified visibility and supports efficient remote operations at scale.",
    "item-1-metric-2-value": "90% Faster",
    "item-1-metric-2-description": "Remote provisioning and configuration streamline onboarding, reduce manual setup, and help teams bring distributed sites online 90% faster.",
    "item-1-metric-3-value": "24/7",
    "item-1-metric-3-description": "Continuous site visibility helps teams detect issues earlier, respond faster, and maintain stable operations across distributed solar PV assets.",
    "item-1-link": "/en-asia-pacific/materials/mirai-electronics-singapores-leading-solar-photovoltaic-manufacturer-builds-next-gen-solar-pv-monitoring-and-metering-system-with-edgehub",
    "item-2-tab-name": "Electronics Manufacturing",
    "item-2-name": "From intelligent to low carbon, Advantech WISE powers YAGEO’s manufacturing transformation.",
    "item-2-metric-1-value": "10%",
    "item-2-metric-1-description": "Real-time equipment visibility and OEE analysis help teams identify utilization gaps, resolve bottlenecks, and improve production performance.",
    "item-2-metric-2-value": "24/7",
    "item-2-metric-2-description": "Continuous monitoring detects abnormal shutdowns early, alerts responsible teams, and enables faster action to reduce unplanned downtime.",
    "item-2-metric-3-value": "Data-Driven",
    "item-2-metric-3-description": "Operational and energy data support smarter decisions, continuous production improvement, and practical long-term carbon-reduction planning.",
    "item-2-link": "/en-asia-pacific/materials/intelligent-low-carbon-wise-iot-yageo-manufacturing-transformation",
    "item-3-tab-name": "Industrial OEM",
    "item-3-name": "Advantech WISE empowers manufacturers with smart, scalable predictive maintenance solutions.",
    "item-3-metric-1-value": "7-Day",
    "item-3-metric-1-description": "Predictive alerts provide up to seven days of advance warning, giving maintenance teams more time to address potential equipment issues.",
    "item-3-metric-2-value": "High-load",
    "item-3-metric-2-description": "Equipment monitoring under high-load conditions helps teams maintain stable operation and respond faster in demanding industrial environments.",
    "item-3-metric-3-value": "Readiness",
    "item-3-metric-3-description": "Real-time health reports and predictive alerts keep maintenance teams informed of equipment conditions and potential abnormalities.",
    "item-3-link": "/en-asia-pacific/materials/tct-integral-wise-iot-predictive-maintenance"
  },
  "en-emea": {
    "title": "Explore Digital O&M Use Cases",
    "item-button": "Learn More",
    "item-1-tab-name": "Solar PV Monitoring",
    "item-1-name": "Singapore’s leading solar PV manufacturer, Mirai Electronics, builds its next-gen remote monitoring system with Advantech WISE.",
    "item-1-metric-1-value": "400 +",
    "item-1-metric-1-description": "Centralized monitoring across 400+ solar PV sites gives operators unified visibility and supports efficient remote operations at scale.",
    "item-1-metric-2-value": "90% Faster",
    "item-1-metric-2-description": "Remote provisioning and configuration streamline onboarding, reduce manual setup, and help teams bring distributed sites online 90% faster.",
    "item-1-metric-3-value": "24/7",
    "item-1-metric-3-description": "Continuous site visibility helps teams detect issues earlier, respond faster, and maintain stable operations across distributed solar PV assets.",
    "item-1-link": "/en-emea/materials/mirai-electronics-singapores-leading-solar-photovoltaic-manufacturer-builds-next-gen-solar-pv-monitoring-and-metering-system-with-edgehub",
    "item-2-tab-name": "Electronics Manufacturing",
    "item-2-name": "From intelligent to low carbon, Advantech WISE powers YAGEO’s manufacturing transformation.",
    "item-2-metric-1-value": "10%",
    "item-2-metric-1-description": "Real-time equipment visibility and OEE analysis help teams identify utilization gaps, resolve bottlenecks, and improve production performance.",
    "item-2-metric-2-value": "24/7",
    "item-2-metric-2-description": "Continuous monitoring detects abnormal shutdowns early, alerts responsible teams, and enables faster action to reduce unplanned downtime.",
    "item-2-metric-3-value": "Data-Driven",
    "item-2-metric-3-description": "Operational and energy data support smarter decisions, continuous production improvement, and practical long-term carbon-reduction planning.",
    "item-2-link": "/en-emea/materials/intelligent-low-carbon-wise-iot-yageo-manufacturing-transformation",
    "item-3-tab-name": "Industrial OEM",
    "item-3-name": "Advantech WISE empowers manufacturers with smart, scalable predictive maintenance solutions.",
    "item-3-metric-1-value": "7-Day",
    "item-3-metric-1-description": "Predictive alerts provide up to seven days of advance warning, giving maintenance teams more time to address potential equipment issues.",
    "item-3-metric-2-value": "High-load",
    "item-3-metric-2-description": "Equipment monitoring under high-load conditions helps teams maintain stable operation and respond faster in demanding industrial environments.",
    "item-3-metric-3-value": "Readiness",
    "item-3-metric-3-description": "Real-time health reports and predictive alerts keep maintenance teams informed of equipment conditions and potential abnormalities.",
    "item-3-link": "/en-emea/materials/tct-integral-wise-iot-predictive-maintenance"
  },
  "ja-jp": {
    "title": "デジタルO&M導入事例",
    "item-button": "もっと知る",
    "item-1-tab-name": "太陽光発電監視",
    "item-1-name": "Mirai ElectronicsはAdvantech WISEを活用し、次世代の太陽光発電遠隔監視システムを構築しました。",
    "item-1-metric-1-value": "400 +",
    "item-1-metric-1-description": "400以上の太陽光発電サイトを一元監視し、遠隔運用を効率化。",
    "item-1-metric-2-value": "90% ↑",
    "item-1-metric-2-description": "リモートプロビジョニングと設定により、デバイスの導入を90％高速化。",
    "item-1-metric-3-value": "24/7",
    "item-1-metric-3-description": "運用状況を可視化し、問題の早期発見と迅速な対応を実現。",
    "item-1-link": "/ja-jp/materials/mirai-electronics-singapores-leading-solar-photovoltaic-manufacturer-builds-next-gen-solar-pv-monitoring-and-metering-system-with-edgehub",
    "item-2-tab-name": "電子製造",
    "item-2-name": "Advantech WISEは、YAGEOのスマートマニュファクチャリングの高度化と低炭素化への移行を支援します。",
    "item-2-metric-1-value": "10%",
    "item-2-metric-1-description": "リアルタイムの可視化とOEEインサイトにより、設備稼働率を向上。",
    "item-2-metric-2-value": "24/7",
    "item-2-metric-2-description": "運用状況を常時可視化し、問題の早期発見と迅速な対応を実現。",
    "item-2-metric-3-value": "データ駆動",
    "item-2-metric-3-description": "運用データを分析し、生産を継続的に最適化し、長期的なサステナビリティの意思決定を支援します。",
    "item-2-link": "/en-int/materials/intelligent-low-carbon-wise-iot-yageo-manufacturing-transformation",
    "item-3-tab-name": "産業OEM",
    "item-3-name": "Advantech WISEは、スマートで拡張性に優れた予知保全ソリューションにより、製造現場の保全業務を高度化します。",
    "item-3-metric-1-value": "7日前に",
    "item-3-metric-1-description": "重大な設備故障に至る前に異常を予測し、早期対応を可能に。",
    "item-3-metric-2-value": "高負荷",
    "item-3-metric-2-description": "過酷な産業環境下で高負荷状態を監視し、安定稼働を維持。",
    "item-3-metric-3-value": "対応力",
    "item-3-metric-3-description": "重要設備の保全連携を強化し、運用対応を迅速化。",
    "item-3-link": "/en-int/materials/tct-integral-wise-iot-predictive-maintenance"
  },
  "zh-tw": {
    "title": "探索數位維運的實際應用",
    "item-button": "了解更多",
    "item-1-tab-name": "太陽光電監控",
    "item-1-name": "新加坡太陽光電領導廠商 Mirai Electronics 攜手 Advantech WISE，打造新一代遠端監控系統。",
    "item-1-metric-1-value": "400 +",
    "item-1-metric-1-description": "集中監控超過 400 座太陽光電場站，提升遠端營運效率。",
    "item-1-metric-2-value": "90% ↑",
    "item-1-metric-2-description": "透過遠端佈建與設定，上線速度提升 90%。",
    "item-1-metric-3-value": "24/7",
    "item-1-metric-3-description": "全天候掌握營運狀況，及早發現問題並加快應變。",
    "item-1-link": "/zh-tw/materials/mirai-electronics-singapores-leading-solar-photovoltaic-manufacturer-builds-next-gen-solar-pv-monitoring-and-metering-system-with-edgehub",
    "item-2-tab-name": "電子製造",
    "item-2-name": "Advantech WISE 助力 YAGEO 深化智慧製造，邁向低碳轉型。",
    "item-2-metric-1-value": "10%",
    "item-2-metric-1-description": "透過即時掌握設備狀況與 OEE 分析，進一步提升設備利用率。",
    "item-2-metric-2-value": "24/7",
    "item-2-metric-2-description": "全天候掌握營運狀況，及早發現問題並加快營運應變。",
    "item-2-metric-3-value": "數據驅動",
    "item-2-metric-3-description": "透過營運數據分析，持續優化製造流程，並為長期永續發展提供決策依據。",
    "item-2-link": "/zh-tw/materials/intelligent-low-carbon-wise-iot-yageo-manufacturing-transformation-tc",
    "item-3-tab-name": "工業設備 OEM",
    "item-3-name": "Advantech WISE 透過智慧且具擴展性的預知維護技術，協助製造業提升設備維護效率。",
    "item-3-metric-1-value": "7 天",
    "item-3-metric-1-description": "在關鍵設備故障前預測異常，爭取提早預防處理、提升韌性。",
    "item-3-metric-2-value": "高負載",
    "item-3-metric-2-description": "即時掌握高負載狀況，確保設備在嚴苛的工業環境下穩定運作。",
    "item-3-metric-3-value": "就緒",
    "item-3-metric-3-description": "強化關鍵資產的維護協調，加快營運應變速度。",
    "item-3-link": "/zh-tw/materials/tct-integral-wise-iot-predictive-maintenance-tc"
  }
}
</i18n>
